// ============================================================
// TMR Portal Admin — aba Contratos dentro do detalhe da empresa
// ============================================================
import { useState } from 'react';
import { Btn } from '../../components/primitives';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Pagination } from '../../components/Pagination';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { CONTRACT_PAGE_SIZE, useContractList } from '../../data/contracts/queries';
import type { AwaitedContract } from '../../data/contracts/queries';
import { contractTitle } from '../../data/contracts/status';
import { ContractRow } from './ContractRow';
import { ContractDetailPanel } from './ContractDetailPanel';
import { NewContractModal } from './NewContractModal';
import type { ContractCompany } from './NewContractModal';

/** Props for {@link CompanyContractsTab}. */
interface CompanyContractsTabProps {
  /** The Empresa whose contracts these are — already resolved by the detail screen. */
  company: ContractCompany;
  /**
   * Whether the contract the backend draws up on its own is still on its way. Owned by the screen, which
   * watches for it whichever tab is open — the count in the tab strip moves with it.
   */
  awaited: AwaitedContract;
  /**
   * Whether the creation modal is open. Controlled by the screen, because the button that opens it lives in
   * the screen header — one primary action per tab, in the same slot as "Editar cadastro". The modal itself
   * stays here, where it can open the new contract's panel once it closes.
   */
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
}

/**
 * One Empresa's contracts, read from the Contracts module.
 *
 * A list rather than a single contract: an Empresa may have any number of them, and an aditivo is a new
 * contract rather than a revision of the existing one (02 §11). That is also why there is no "contrato
 * vigente" summary — nothing in the record says which of N is the current one, and the validity dates the
 * mockup showed belong to the provider, not to tmr_v2.
 */
export function CompanyContractsTab({
  company,
  awaited,
  creating,
  onCreatingChange,
}: CompanyContractsTabProps) {
  const [page, setPage] = useState(1);
  const [openContractId, setOpenContractId] = useState<string | null>(null);

  const canRead = usePermission(PERMISSIONS.contractsRead);
  const canManage = usePermission(PERMISSIONS.contractsManage);
  const query = useContractList({ partyId: company.id, page });

  // The header carries this action for a tab that already has contracts. It is repeated in the empty state
  // because that is the one moment the screen has nothing else to offer. A switched-off Empresa gets it in
  // neither place: the server refuses, and the header hides it for the same reason — and so does an Empresa
  // whose automatic contract is still on its way, where creating one by hand produces a duplicate.
  const newContractButton = canManage && company.active && !awaited.awaiting ? (
    <Btn variant="primary" icon={Icons.plus} onClick={() => onCreatingChange(true)}>
      Criar contrato
    </Btn>
  ) : null;

  // Without contracts.read the list would 403 on every render. Saying so beats an error state that reads
  // like a fault — the server is right to refuse (06 §6).
  if (!canRead) {
    return (
      <div className="pa-card">
        <EmptyState
          message="Você não tem permissão para ver os contratos desta empresa."
          hint="Peça o acesso a quem administra os papéis."
        />
      </div>
    );
  }

  const items = query.data?.items ?? [];
  const total = query.data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / CONTRACT_PAGE_SIZE));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {query.isPending && <Skeleton rows={4} />}

      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}

      {query.data && items.length === 0 && (
        <div className="pa-card">
          {awaited.awaiting ? (
            <AwaitingContract />
          ) : (
            <EmptyState
              message="Esta empresa ainda não tem contrato."
              hint={
                awaited.gaveUp
                  ? 'O contrato automático desta empresa não apareceu. Ele ainda pode chegar — atualize para conferir, ou crie um agora.'
                  : canManage
                    ? 'Criar um contrato o deixa pendente; enviar para assinatura é o passo seguinte.'
                    : 'Você não tem permissão para criar contratos.'
              }
              action={
                awaited.gaveUp ? (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Btn variant="secondary" icon={Icons.swap} onClick={awaited.refresh}>
                      Atualizar
                    </Btn>
                    {newContractButton}
                  </div>
                ) : (
                  newContractButton
                )
              }
            />
          )}
        </div>
      )}

      {items.length > 0 && (
        <>
          {/* Only the count here. Creating lives in the screen header, next to where "Editar cadastro" sits
              on the other tab — one primary action per tab, in one place. */}
          <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            {total === 1 ? '1 contrato' : `${total} contratos`}
          </span>

          <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
            {items.map((contract) => (
              <ContractRow
                key={contract.id}
                contract={contract}
                canManage={canManage}
                onOpen={() => setOpenContractId(contract.id)}
                lead={contractTitle(contract)}
              />
            ))}
          </div>

          <Pagination page={page} pageCount={pageCount} total={total} onChange={setPage} />
        </>
      )}

      <ContractDetailPanel
        contractId={openContractId}
        canManage={canManage}
        onClose={() => setOpenContractId(null)}
      />

      {creating && (
        <NewContractModal
          company={company}
          onClose={() => onCreatingChange(false)}
          onCreated={(contract) => setOpenContractId(contract.id)}
        />
      )}
    </div>
  );
}

/**
 * What the tab shows in the minute between an Empresa being registered and its contract existing.
 *
 * The backend draws the contract up itself, once the Empresa finishes replicating to the legacy (03 §12) —
 * so this is not an empty list, it is a list that is about to fill. Saying "esta empresa ainda não tem
 * contrato" there was true and useless: the operator read it as final and either created a second contract
 * by hand or reloaded the page until the first one showed up.
 *
 * It borrows the vocabulary the replication callout already uses for a backend operation that finishes
 * later — pendente, com a promessa de que a tela se atualiza sozinha (skill `frontend-architecture`,
 * Regra 7) — rather than inventing a second way to say "espere".
 */
function AwaitingContract() {
  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '38px 24px',
        color: 'var(--stone-400)',
      }}
    >
      <span className="pa-pulse" style={{ display: 'flex' }}>
        <StrokeIcon d={Icons.clock} size={22} />
      </span>
      <p style={{ margin: '10px 0 0', fontFamily: 'var(--font-sans)', fontSize: 14.5, color: 'var(--fg-muted)' }}>
        O contrato desta empresa está sendo criado.
      </p>
      <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--fg-subtle)', maxWidth: 420 }}>
        Ele nasce sozinho assim que a empresa termina de ser replicada para o sistema legado, e aparece aqui
        em alguns segundos — esta tela se atualiza sozinha, não é preciso recarregar.
      </p>
      <div style={{ width: 220, marginTop: 16 }}>
        <div
          aria-hidden="true"
          style={{
            height: 6,
            borderRadius: 999,
            background: 'linear-gradient(90deg, var(--cream-100) 25%, var(--cream-200) 50%, var(--cream-100) 75%)',
            backgroundSize: '200% 100%',
            animation: 'paShimmer 1.4s var(--ease-out) infinite',
          }}
        />
      </div>
    </div>
  );
}
